/**
 * TA 的一位朋友（新版详情，UF-01「宠物交流」）：数据来自 GET /pets/{pet_id}/friends/{friend_id}。二级页，按明确入口回手机或“TA 的朋友”。
 * 路由 /communicator/friends/:friendId（通讯器模块挂，朋友编号来自地址，宠物是当前家庭上下文里的那一只；切宠时外壳退回朋友列表）。
 *
 * 一页四块：
 * - 头部：名字、有没有来回（聊过 / 打过招呼 / 没接上 / 遇见过）、星球居民标签、“已不是朋友”、宠物 · 猫、最近往来哪天；宠物朋友多一个去它主页的链接；
 * - 相遇：旧的相遇记录（同地 N 次：同一时间在同一个地方；到过哪些地方）和现场相遇（当面 N 次：面对面遇上，聊没聊起来都算）分开写，
 *   不相加、不写“其中”；当面 0 次写“当面：还没有”；星球居民没有当面这一项（c84a D1736-4，2531 R25 写法 A）；
 * - 一起经历的事：哪天、在哪、说上话没有；
 * - 聊天：最近 20 场，每场是见面还是消息、哪天、分类（聊过 / 打过招呼 / 没接上 / 等回应 / 联系中）、送到与收到的句数、收尾（结束了 / 没接上话 / 中途停下 / 已过期）。
 * 不给原话：要看原话得走聊天回放，那一页还没有，所以聊天这里不做成可点的样子。
 * 每段为 null 是不知道：整段不显示；为空是没有：写一句“还没有……”。四块都不知道时写一句“暂时还看不到更多往来”。
 * 找不到这位朋友（以及看不到的几种情况）后端都是同一个 404，这里也只说一句“这里看不到了”，不猜是哪一种。
 * 只看不动：没有加、删、批准朋友的按钮（宠物自己做主）。
 */
import { useId, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router";
import type { PetFriendDetail } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { Chip, EmptyState, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { readSceneEntry, SourceBack } from "@/shared/ui/sceneNavigation";
import { friendProfileHref } from "./friends";
import {
  FRIENDS_LIST_HREF,
  chatCountsText,
  chatEndingLabel,
  chatStateOf,
  chatTitle,
  encountersView,
  episodeText,
  friendExchangeOf,
  friendName,
  historyView,
  isResident,
  kindText,
  lastInteractionText,
  placesOf,
  relationLabel,
  type MeetingView,
} from "./petFriends";
import { FriendParts } from "./FriendParts";
import { usePetFriendDetail } from "./usePetFriends";
import "./petFriends.css";

function Meeting({ view, extra }: { view: MeetingView; extra?: ReactNode }) {
  return (
    <li className="ps-friend-detail__row">
      <strong>{view.headline}</strong>
      {view.note ? <span className="ps-friend-detail__note-line">{view.note}</span> : null}
      {view.last ? <span>{view.last}</span> : null}
      {view.first ? <span>{view.first}</span> : null}
      {extra}
    </li>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="ps-friend-detail__section" aria-labelledby={id}>
      <h2 id={id} className="ps-section-title">
        {title}
      </h2>
      {children}
    </section>
  );
}

function FriendDetail({ friend }: { friend: PetFriendDetail }) {
  const nameId = useId();
  const name = friendName(friend.name);
  const exchange = friendExchangeOf(friend.exchange);
  const relation = relationLabel(friend.relation);
  const resident = isResident(friend.kind);
  // 每一项是一个不折行的小块，窄屏只在“·”处换行（不拆成“最近 / 往来”）
  const facts = [kindText(friend.kind, friend.species), lastInteractionText(friend.last_interaction_at)].filter((text): text is string => Boolean(text));
  const profile = friendProfileHref(friend);
  const history = historyView(friend.meetings?.history);
  const places = placesOf(friend.meetings?.history);
  const encounters = encountersView(friend.meetings?.encounters, resident);
  const episodes = friend.episodes ?? null;
  const chats = friend.chats ?? null;
  const nothingKnown = !history && !encounters && episodes === null && chats === null;

  return (
    <>
      <section className="ps-card ps-friend-detail__head" aria-labelledby={nameId}>
        <span className="ps-friend-detail__avatar" aria-hidden="true">
          <PetPortrait name={name} size={64} />
        </span>
        <div className="ps-friend-detail__who">
          {/* 顶栏标题「TA 的朋友」是这一页的 h1（UI-BASE-01b），名字降成 h2 */}
          <h2 id={nameId} className="ps-friend-detail__name">
            {name}
          </h2>
          {exchange || resident || relation ? (
            <div className="ps-friend-detail__chips">
              {exchange ? <Chip tone={exchange.tone}>{exchange.label}</Chip> : null}
              {resident ? (
                <Chip tone="sun" icon="planet">
                  星球居民
                </Chip>
              ) : null}
              {relation ? <Chip>{relation}</Chip> : null}
            </div>
          ) : null}
          {facts.length ? <p className="ps-friend-detail__facts"><FriendParts parts={facts} /></p> : null}
          {profile ? (
            <Link className="ps-friend-detail__profile" to={profile}>
              {`看看${name}的主页`}
              <Icon name="chevron" size={14} />
            </Link>
          ) : null}
        </div>
      </section>

      {history || encounters ? (
        <Section title="相遇">
          <ul className="ps-friend-detail__rows">
            {history ? <Meeting view={history} extra={places.length ? <span>{`同一时间到过：${places.join("、")}`}</span> : null} /> : null}
            {encounters ? <Meeting view={encounters} /> : null}
          </ul>
        </Section>
      ) : null}

      {episodes ? (
        <Section title="一起经历的事">
          {episodes.length ? (
            <ul className="ps-friend-detail__rows">
              {episodes.map((episode, index) => (
                <li key={`${episode.thread_id}|${index}`} className="ps-friend-detail__row">
                  <span>{episodeText(episode)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ps-friend-detail__empty">还没有一起经历的事。</p>
          )}
        </Section>
      ) : null}

      {chats ? (
        <Section title="聊天">
          {chats.items?.length ? (
            <ul className="ps-friend-detail__rows">
              {chats.items.map((chat) => {
                const state = chatStateOf(chat);
                const counts = chatCountsText(chat);
                const ending = chatEndingLabel(chat);
                return (
                  <li key={chat.thread_id} className="ps-friend-detail__row ps-friend-chat">
                    <div className="ps-friend-chat__head">
                      <strong>{chatTitle(chat)}</strong>
                      {state ? <Chip tone={state.tone}>{state.label}</Chip> : null}
                    </div>
                    {counts ? <span>{counts}</span> : null}
                    {ending ? <span className="ps-friend-chat__end">{ending}</span> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="ps-friend-detail__empty">还没有聊过天。</p>
          )}
          {chats.more ? <p className="ps-friend-detail__note">这里只列最近 20 场。</p> : null}
        </Section>
      ) : null}

      {nothingKnown ? <p className="ps-friend-detail__empty">暂时还看不到更多往来。</p> : null}
    </>
  );
}

function NotHere({ fromPhone = false, witnessedPhonePush = false }: { fromPhone?: boolean; witnessedPhonePush?: boolean }) {
  const navigate = useNavigate();
  const phone = "/communicator?channel=phone";
  return (
    <EmptyState
      icon="lock"
      title="这里看不到了"
      action={
        fromPhone ? (
          <SourceBack fallback={phone} levelParent={phone} className="ps-btn ps-btn--ghost ps-btn--sm" onClick={(event) => {
            if (!witnessedPhonePush || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            void navigate(-1);
          }}>
            返回手机
          </SourceBack>
        ) : (
          <Link to={FRIENDS_LIST_HREF} className="ps-btn ps-btn--ghost ps-btn--sm">
            回到朋友列表
          </Link>
        )
      }
    >
      可能已经找不到这位朋友，或者这里没有向你开放。
    </EmptyState>
  );
}

export function PetFriendDetailPage({ petId, friendId, pending = false }: { petId: string | null; friendId: string | null; /** 宠物还在读（路由组件给）：先显示加载 */ pending?: boolean }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const sources = params.getAll("from");
  const fromPhone = sources.length === 1 && sources[0] === "phone";
  const phone = "/communicator?channel=phone";
  // 两个路由的外壳会换挂；仅本次真实 PUSH 且携带手机父层时沿用顶栏 POP。
  // 刷新/前进/深链（POP）与 REPLACE 不把一个非默认 key 当作后退历史。
  const witnessedPhonePush = fromPhone && navigationType === "PUSH" && readSceneEntry(location.state, null)?.parent === phone;
  // 宠物还在读时不请求（哪怕调用方给了 petId）：等读到了再按那一只请求
  const detail = usePetFriendDetail(pending ? null : petId, friendId);

  let content: ReactNode;
  if (pending) {
    content = <LoadingState lines={2} label="翻看 TA 和这位朋友的往来…" />;
  } else if (!petId || !friendId) {
    content = <NotHere fromPhone={fromPhone} witnessedPhonePush={witnessedPhonePush} />;
  } else if (detail.isError) {
    const error = toApiError(detail.error);
    if (error.isCapabilityUnavailable || error.unknownRoute) {
      content =
        env.dataMode === "fixture" ? (
          <EmptyState icon="info" title="演示里还没有朋友记录">
            朋友要 TA 真的在外面遇见，才会记在这里；演示世界里先空着。
          </EmptyState>
        ) : (
          <EmptyState icon="info" title="暂时看不到这位朋友">
            过一会儿再来看看。
          </EmptyState>
        );
    } else if (error.isNotFound) {
      content = <NotHere fromPhone={fromPhone} witnessedPhonePush={witnessedPhonePush} />;
    } else {
      content = <ErrorState error={detail.error} onRetry={() => void detail.refetch()} retrying={detail.isFetching} />;
    }
  } else if (!detail.data) {
    content = <LoadingState lines={2} label="翻看 TA 和这位朋友的往来…" />;
  } else {
    content = <FriendDetail friend={detail.data} />;
  }

  return (
    <Page bare className="ps-friend-detail-page">
      {fromPhone ? (
        <TopBar title="TA 的朋友" back={phone} backLabel="返回手机" sourceBack={witnessedPhonePush ? undefined : { fallback: phone, levelParent: phone }} />
      ) : (
        <TopBar title="TA 的朋友" back={FRIENDS_LIST_HREF} />
      )}
      {content}
    </Page>
  );
}
